import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import { PageHero, CTABand, SectionHead } from "../components/site/Blocks";
import { Reveal } from "../components/site/Motion";
import { SERVICES, PROCESS, IMAGES } from "../data/content";

const ServiceRow = ({ s, i }) => (
  <article id={s.slug} data-testid={`service-${s.slug}`} className="grid scroll-mt-28 gap-8 border-t border-black/15 py-12 lg:grid-cols-12 lg:py-16">
    <Reveal className="lg:col-span-1"><p className="font-mono text-sm text-[var(--muted)]">0{i + 1}</p></Reveal>
    <Reveal className="lg:col-span-5">
      <h3 className="text-3xl font-semibold tracking-[-0.03em] sm:text-5xl">{s.title}</h3>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-6">
      <p className="text-lg leading-relaxed text-[var(--muted)]">{s.short}</p>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2">
        {s.points.map((p) => (
          <li key={p} className="flex items-center gap-3 text-sm font-medium">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--cobalt)] text-white"><Check className="h-3.5 w-3.5" /></span>
            {p}
          </li>
        ))}
      </ul>
    </Reveal>
  </article>
);

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <main data-testid="services-page">
      <PageHero
        testid="services-hero"
        label="Services"
        lines={["Operations and", <span className="font-serif-i">consultancy,</span>, "under one roof."]}
        intro="From survey programming to boardroom strategy, we run the full research lifecycle — or plug into yours exactly where you need us."
        image={IMAGES.analyst}
      />
      <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32" data-testid="services-list">
        {SERVICES.map((s, i) => <ServiceRow key={s.slug} s={s} i={i} />)}
      </section>
      <section className="bg-[var(--ink)] py-24 text-[var(--paper)] sm:py-32" data-testid="services-qa">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-400">Quality assurance workflow</p>
          <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1] tracking-[-0.035em] sm:text-6xl">Quality is built in, <span className="font-serif-i">not bolted on.</span></h2>
          <div className="mt-16 grid gap-px overflow-hidden rounded-[4px] bg-white/15 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1} className="bg-[var(--ink)] p-8">
                <p className="font-mono text-xs text-[var(--cobalt)] brightness-150">{p.n}</p>
                <h3 className="mt-12 text-2xl font-semibold">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-[1400px] px-5 pt-24 sm:px-8 sm:pt-32">
        <SectionHead label="Engagement models" title={["Work with us", <span className="font-serif-i">your way.</span>]} />
        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {[["Full-service", "We own the project end to end, from scoping to final presentation."], ["Operations partner", "We power your fieldwork, programming and data processing behind the scenes."], ["Consultancy", "Domain experts on call to shape strategy and interpret findings."]].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08} className="rounded-[4px] border border-black/15 p-8" data-testid={`engagement-${i}`}>
              <h3 className="text-2xl font-semibold tracking-tight">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <CTABand />
    </main>
  );
}
